import React from 'react';
import { CustomNumberField } from '../../../../../components/FormFields/CustomField/CustomNumberField';
import { TransactionFeeScheduleItem } from '../../../../../slices/marketplace/pricing/models/transaction-fee-schedule-item.model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import Helpers from '../../../../../utils/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormikErrors } from 'formik';
import './TransactionFeeScheduleTable.scss';
import { MarketplacePercentField } from '../MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import {
    FeeRefundStatus,
    FeeRefundStatuses,
} from '../../../../../slices/marketplace/pricing/models/fee-refund-status.enum';

type BrokerageTypeKey =
    | 'discountBrokeragePricing'
    | 'fullServiceBrokeragePricing';

interface TransactionFeeScheduleTableProps {
    pricing: MarketplacePricing;
    brokerageType: BrokerageTypeKey;
    label: string;
    setValue: (
        field: string,
        value: any,
        shouldValidate?: boolean,
        rowAddedIndex?: number,
        rowDeletedIndex?: number
    ) => Promise<void | FormikErrors<MarketplacePricing>>;
}

const TransactionFeeScheduleTable = (
    props: TransactionFeeScheduleTableProps
) => {
    const { brokerageType } = props;
    const schedule = props.pricing[brokerageType].transactionFeeSchedule;
    const isFsb = brokerageType === 'fullServiceBrokeragePricing';

    const deleteRow = (index: number) => {
        const newState = schedule.filter((_, idx) => idx !== index);
        props.setValue(
            `${brokerageType}.transactionFeeSchedule`,
            newState,
            undefined,
            undefined,
            index
        );
    };

    const addRow = (index: number) => {
        const newIndex = Math.max(...schedule.map((f) => f.index)) + 1;
        const newState = [...schedule];
        newState.splice(index + 1, 0, {
            feePercent: 0,
            revenueMax: 0,
            index: newIndex,
            idNdaFeePerBuyer: 0,
            dataNdaFeePerBuyer: 0,
            financialModelFeePerBuyer: 0,
            monthlyRetainerFee: 0,
            lateFeeFirstOccurrence: 0,
            lateFeeAdditionalOccurrence: 0,
            suspensionFee: 0,
            tempoChangeFee: 0,
        });
        props.setValue(
            `${brokerageType}.transactionFeeSchedule`,
            newState,
            undefined,
            index + 1
        );
    };

    const renderFeeRow = (fee: TransactionFeeScheduleItem, index: number) => {
        const itemKey = `${brokerageType}.transactionFeeSchedule[${index}]`;
        const previousItem = index > 0 ? schedule[index - 1] : undefined;
        const isLastRow = schedule.length === index + 1;

        return (
            <tr key={index}>
                <td className="col-revenue">
                    <CustomNumberField
                        formKey={`${itemKey}.revenueMin`}
                        value={Helpers.formatToDollarValueWithCentsIfExist(
                            previousItem ? Number(previousItem.revenueMax) : 0
                        )}
                        updateValue={() => {}}
                        suffix={''}
                        disabled={true}
                    />
                </td>
                <td className="col-revenue">
                    {!isLastRow ? (
                        <MarketplaceCurrencyField
                            name={`${itemKey}.revenueMax`}
                            value={fee.revenueMax}
                            setFormValue={(name, value) => props.setValue(name, value)}
                        />
                    ) : (
                        <CustomNumberField
                            formKey={`${itemKey}.revenueMax`}
                            value={
                                '> ' +
                                Helpers.formatToDollarValueWithCentsIfExist(
                                    schedule[index - 1].revenueMax
                                )
                            }
                            updateValue={() => {}}
                            suffix={''}
                            disabled={true}
                        />
                    )}
                </td>
                <td className="col-fee">
                    <MarketplacePercentField
                        name={`${itemKey}.feePercent`}
                        value={schedule[index].feePercent}
                        setValue={props.setValue}
                    />
                </td>
                <td className="col-fee">
                    <MarketplaceCurrencyField
                        name={`${itemKey}.idNdaFeePerBuyer`}
                        value={fee.idNdaFeePerBuyer}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                <td className="col-fee">
                    <MarketplaceCurrencyField
                        name={`${itemKey}.dataNdaFeePerBuyer`}
                        value={fee.dataNdaFeePerBuyer}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                <td className="col-fee">
                    <MarketplaceCurrencyField
                        name={`${itemKey}.financialModelFeePerBuyer`}
                        value={fee.financialModelFeePerBuyer}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                {isFsb && (
                    <td className="col-fee">
                        <MarketplaceCurrencyField
                            name={`${itemKey}.monthlyRetainerFee`}
                            value={fee.monthlyRetainerFee ?? 0}
                            setFormValue={(name, value) => props.setValue(name, value)}
                        />
                    </td>
                )}
                <td className="col-late-fee">
                    <small className="field-label">1st occurrence</small>
                    <MarketplaceCurrencyField
                        name={`${itemKey}.lateFeeFirstOccurrence`}
                        value={fee.lateFeeFirstOccurrence}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                    <small className="field-label">Each add. occurrence</small>
                    <MarketplaceCurrencyField
                        name={`${itemKey}.lateFeeAdditionalOccurrence`}
                        value={fee.lateFeeAdditionalOccurrence}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                <td className="col-fee">
                    <MarketplaceCurrencyField
                        name={`${itemKey}.suspensionFee`}
                        value={fee.suspensionFee ?? 0}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                <td className="col-fee">
                    <MarketplaceCurrencyField
                        name={`${itemKey}.tempoChangeFee`}
                        value={fee.tempoChangeFee}
                        setFormValue={(name, value) => props.setValue(name, value)}
                    />
                </td>
                <td>
                    <div className="actions-cell">
                        {schedule.length > 2 && (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="delete-btn"
                                onClick={() => deleteRow(index)}
                            />
                        )}
                        <FontAwesomeIcon
                            icon={faPlus}
                            className="delete-btn"
                            onClick={() => addRow(index)}
                        />
                    </div>
                </td>
            </tr>
        );
    };

    const fees = props.pricing[brokerageType].transactionFees;

    const refundableSelect = (fieldName: string, value: number | undefined) => (
        <select
            value={String(value ?? FeeRefundStatus.NonRefundable)}
            onChange={(e) => props.setValue(`${brokerageType}.transactionFees.${fieldName}`, Number(e.target.value))}
        >
            {FeeRefundStatuses.map((item) => (
                <option value={String(item.id)} key={item.id}>{item.name}</option>
            ))}
        </select>
    );

    const creditablePercent = (fieldName: string, value: number) => (
        <MarketplacePercentField
            name={`${brokerageType}.transactionFees.${fieldName}`}
            value={value}
            setValue={(name, val) => props.setValue(name, Number(val))}
        />
    );

    return (
        <div className="transaction-fee-schedule-table">
            <div className="mb-1">
                <label className="bold-label">{props.label}</label>
            </div>
            <div className="table-scroll">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th className="col-revenue">Revenue, Min</th>
                            <th className="col-revenue">Revenue, Max</th>
                            <th className="col-fee">Transaction Fee</th>
                            <th className="col-fee">
                                <div className="label-with-tooltip">
                                    Buyer ID NDA Fee
                                    <InfoTooltip
                                        idText={`${brokerageType}-idNdaFeePerBuyer-header`}
                                        text={"Once a buyer signs an ID NDA with the seller, the seller's indentity is disclosed to that buyer. The seller selects which of the interested buyers it wishes to send an ID NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the ID NDA."}
                                    />
                                </div>
                            </th>
                            <th className="col-fee">
                                <div className="label-with-tooltip">
                                    {isFsb ? 'PIP' : 'PIQ'} Provision Fee
                                    <InfoTooltip
                                        idText={`${brokerageType}-dataNdaFeePerBuyer-header`}
                                        text={`Buyers must sign a Data NDA before they are eligible to receive either an practice ${isFsb ? 'investment presentation ("PIP")' : 'information questionnaire ("PIQ")'} or the detailed financial model. The seller selects which of the interested buyers it wishes to send a Data NDA. The seller is then charged ONLY for the number of buyers that actually elect to sign the Data NDA.`}
                                    />
                                </div>
                            </th>
                            <th className="col-fee">
                                <div className="label-with-tooltip">
                                    DFM Provision Fee
                                    <InfoTooltip
                                        idText={`${brokerageType}-financialModelFeePerBuyer-header`}
                                        text={'The seller selects which of the interested buyers it wishes to allow to see the detailed financial model. The seller is then charged for each of these buyers which view the detailed financial model.'}
                                    />
                                </div>
                            </th>
                            {isFsb && (
                                <th className="col-fee">
                                    <div className="label-with-tooltip">
                                        Monthly Engagement Fee
                                        <InfoTooltip
                                            idText={`${brokerageType}-monthlyRetainerFee-header`}
                                            text={'An engagement fee is charged at the beginning of each month, starting at the effective date of the engagement. During any period for which the seller elects to suspend the engagement, this fee is reduce by 50% for the duration of that suspendion.'}
                                        />
                                    </div>
                                </th>
                            )}
                            <th className="col-late-fee">
                                <div className="label-with-tooltip">
                                    Late Fee
                                    <InfoTooltip
                                        idText={`${brokerageType}-lateFee-header`}
                                        text={"Late fees are charged each time the seller does not complete a step by that step's deadline."}
                                    />
                                </div>
                            </th>
                            <th className="col-fee">
                                <div className="label-with-tooltip">
                                    Monthly Suspension Fee
                                    <InfoTooltip
                                        idText={`${brokerageType}-suspensionFee-header`}
                                        text={'A suspension fee is charged for each month the engagement has been suspended by the seller.'}
                                    />
                                </div>
                            </th>
                            <th className="col-fee">Tempo Change Fee</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {schedule.map((f, index) => renderFeeRow(f, index))}
                        <tr className="first-x-row">
                            <td className="col-revenue">
                                <small className="field-label">First X Buyers count as 1</small>
                            </td>
                            <td className="col-revenue" />
                            <td className="col-fee" />
                            <td className="col-fee">
                                <input
                                    type="number"
                                    min={0}
                                    value={fees.idNdaFirstXCount}
                                    onChange={(e) => props.setValue(`${brokerageType}.transactionFees.idNdaFirstXCount`, Number(e.target.value))}
                                />
                            </td>
                            <td className="col-fee">
                                <input
                                    type="number"
                                    min={0}
                                    value={fees.dataNdaFirstXCount}
                                    onChange={(e) => props.setValue(`${brokerageType}.transactionFees.dataNdaFirstXCount`, Number(e.target.value))}
                                />
                            </td>
                            <td className="col-fee">
                                <input
                                    type="number"
                                    min={0}
                                    value={fees.financialModelFirstXCount}
                                    onChange={(e) => props.setValue(`${brokerageType}.transactionFees.financialModelFirstXCount`, Number(e.target.value))}
                                />
                            </td>
                            {isFsb && <td className="col-fee" />}
                            <td className="col-late-fee" />
                            <td className="col-fee" />
                            <td className="col-fee" />
                            <td />
                        </tr>
                        <tr className="refundable-row">
                            <td className="col-revenue">
                                <small className="field-label">Refundable</small>
                            </td>
                            <td className="col-revenue" />
                            <td className="col-fee" />
                            <td className="col-fee">{refundableSelect('idNdaFeeRefundable', fees.idNdaFeeRefundable)}</td>
                            <td className="col-fee">{refundableSelect('dataNdaFeeRefundable', fees.dataNdaFeeRefundable)}</td>
                            <td className="col-fee">{refundableSelect('financialModelFeeRefundable', fees.financialModelFeeRefundable)}</td>
                            {isFsb && <td className="col-fee">{refundableSelect('monthlyRetainerFeeRefundable', fees.monthlyRetainerFeeRefundable ?? FeeRefundStatus.NonRefundable)}</td>}
                            <td className="col-late-fee"><small className="field-label">No</small></td>
                            <td className="col-fee">{refundableSelect('suspensionFeeRefundable', fees.suspensionFeeRefundable ?? FeeRefundStatus.NonRefundable)}</td>
                            <td className="col-fee">{refundableSelect('tempoChangeFeeRefundable', fees.tempoChangeFeeRefundable ?? FeeRefundStatus.NonRefundable)}</td>
                            <td />
                        </tr>
                        <tr className="creditable-row">
                            <td className="col-revenue">
                                <small className="field-label">Creditable</small>
                            </td>
                            <td className="col-revenue" />
                            <td className="col-fee" />
                            <td className="col-fee">{creditablePercent('idNdaFeeCreditable', fees.idNdaFeeCreditable)}</td>
                            <td className="col-fee">{creditablePercent('dataNdaFeeCreditable', fees.dataNdaFeeCreditable)}</td>
                            <td className="col-fee">{creditablePercent('financialModelFeeCreditable', fees.financialModelFeeCreditable)}</td>
                            {isFsb && <td className="col-fee">{creditablePercent('monthlyRetainerFeeCreditable', fees.monthlyRetainerFeeCreditable ?? 0)}</td>}
                            <td className="col-late-fee"><small className="field-label">No</small></td>
                            <td className="col-fee">{creditablePercent('suspensionFeeCreditable', fees.suspensionFeeCreditable ?? 0)}</td>
                            <td className="col-fee">{creditablePercent('tempoChangeFeeCreditable', fees.tempoChangeFeeCreditable ?? 0)}</td>
                            <td />
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TransactionFeeScheduleTable;
